import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface MicrosoftGptImage15Params {
  /**
   * 图片生成提示词
   */
  prompt: string;
  /**
   * 生成图片尺寸
   * @default "1024x1024"
   * @values 1024×1024 | 1024×1536 | 1536×1024
   */
  size?: '1024×1024' | '1024×1536' | '1536×1024';
  /**
   * 图片质量
   * @default "medium"
   * @values low | medium | high
   */
  quality?: 'low' | 'medium' | 'high';
  /**
   * 生成图片数量
   * @default 1
   * @range 1 - 10
   */
  n?: number;
  /**
   * 输出压缩格式(可选)
   */
  output_compression?: number;
}

export interface MicrosoftGptImage15Resource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * microsoft_gpt_image_15
 *
 * <p>Microsoft GPT-Image-1.5 文本生成图片</p>
 * <p>API 文档: https://learn.microsoft.com/en-us/azure/ai-foundry/openai/how-to/dall-e<br>
 * <a href="https://platform.openai.com/docs/api-reference/images/create">https://platform.openai.com/docs/api-reference/images/create</a></p>
 * 
 *
 * @example
 * ```typescript
 * import { initSeacloud, microsoftGptImage15 } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await microsoftGptImage15({
 *   // 参数见 MicrosoftGptImage15Params 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 MicrosoftGptImage15Params 接口定义
 * @returns 生成的资源列表
 */
export async function microsoftGptImage15(
  params: MicrosoftGptImage15Params,
): Promise<MicrosoftGptImage15Resource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'microsoft_gpt_image_15',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: MicrosoftGptImage15Resource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
